import * as THREE from 'three';

function createParticleTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext('2d');
  const glow = context.createRadialGradient(16, 16, 0, 16, 16, 16);
  glow.addColorStop(0, 'rgba(255,255,255,1)');
  glow.addColorStop(0.35, 'rgba(255,255,255,.9)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, 32, 32);
  return new THREE.CanvasTexture(canvas);
}

export class Effects {
  constructor(scene, { lowPower = false } = {}) {
    this.scene = scene;
    this.lowPower = lowPower;
    this.bursts = [];
    this.projectiles = [];
    this.projectileCursor = 0;
    this.burstCursor = 0;
    this.tempStart = new THREE.Vector3();
    this.tempEnd = new THREE.Vector3();
    this.up = new THREE.Vector3(0, 1, 0);
    this.burstTexture = createParticleTexture();
    this.createProjectilePool();
    this.createBurstPool();
  }

  createProjectilePool() {
    const count = this.lowPower ? 24 : 48;
    const geometry = new THREE.CylinderGeometry(1, 1, 1, 6, 1, true);
    const blue = new THREE.MeshBasicMaterial({
      color: '#62b5ff', transparent: true, opacity: 0.92,
      blending: THREE.AdditiveBlending, depthWrite: false
    });
    const red = blue.clone();
    red.color.set('#ff5268');
    for (let index = 0; index < count; index += 1) {
      const mesh = new THREE.Mesh(geometry, blue);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.renderOrder = 12;
      this.scene.add(mesh);
      this.projectiles.push({
        mesh, active: false, life: 0, direction: new THREE.Vector3(), speed: 0
      });
    }
    this.projectileGeometry = geometry;
    this.projectileMaterials = { blue, red };
  }

  createBurstPool() {
    const poolSize = this.lowPower ? 5 : 10;
    const particleCapacity = this.lowPower ? 18 : 42;
    for (let poolIndex = 0; poolIndex < poolSize; poolIndex += 1) {
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(particleCapacity * 3);
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setDrawRange(0, 0);
      const material = new THREE.PointsMaterial({
        color: '#ffbd62',
        map: this.burstTexture,
        size: 6,
        sizeAttenuation: false,
        transparent: true,
        opacity: 0,
        alphaTest: 0.02,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      const particles = new THREE.Points(geometry, material);
      particles.visible = false;
      particles.frustumCulled = false;
      particles.renderOrder = 11;
      this.scene.add(particles);
      this.bursts.push({
        particles,
        positions,
        velocities: new Float32Array(particleCapacity * 3),
        capacity: particleCapacity,
        count: 0,
        active: false,
        life: 0,
        gravity: 5
      });
    }
  }

  tracer(from, to, color = '#8fc9ff', missile = false) {
    const start = this.tempStart.set(from.x, from.y, from.z);
    const direction = this.tempEnd.set(to.x, to.y, to.z).sub(start).normalize();
    const projectile = this.projectiles.find((item) => !item.active)
      ?? this.projectiles[this.projectileCursor++ % this.projectiles.length];
    const length = missile ? 34 : 20;
    projectile.active = true;
    projectile.life = missile ? 0.82 : 0.34;
    projectile.speed = missile ? 840 : 1380;
    projectile.direction.copy(direction);
    projectile.mesh.material = color.toLowerCase().includes('ff4')
      ? this.projectileMaterials.red
      : this.projectileMaterials.blue;
    projectile.mesh.scale.set(missile ? 1.05 : 0.34, length, missile ? 1.05 : 0.34);
    projectile.mesh.quaternion.setFromUnitVectors(this.up, direction);
    projectile.mesh.position.copy(start).addScaledVector(direction, length * 0.5 + 7);
    projectile.mesh.visible = true;
  }

  burst(position, color = '#ffbd62', count = 26, force = 18) {
    const burst = this.bursts.find((item) => !item.active)
      ?? this.bursts[this.burstCursor++ % this.bursts.length];
    count = Math.min(count, burst.capacity);
    for (let index = 0; index < count; index += 1) {
      const offset = index * 3;
      burst.positions[offset] = 0;
      burst.positions[offset + 1] = 0;
      burst.positions[offset + 2] = 0;
      burst.velocities[offset] = (Math.random() - 0.5) * force;
      burst.velocities[offset + 1] = (Math.random() - 0.2) * force;
      burst.velocities[offset + 2] = (Math.random() - 0.5) * force;
    }
    burst.count = count;
    burst.life = 1;
    burst.active = true;
    burst.particles.position.set(position.x, position.y, position.z);
    burst.particles.geometry.setDrawRange(0, count);
    burst.particles.geometry.attributes.position.needsUpdate = true;
    burst.particles.material.color.set(color);
    burst.particles.material.opacity = 1;
    burst.particles.material.size = 6;
    burst.particles.visible = true;
  }

  update(dt) {
    for (const projectile of this.projectiles) {
      if (!projectile.active) continue;
      projectile.life -= dt;
      projectile.mesh.position.addScaledVector(projectile.direction, projectile.speed * dt);
      if (projectile.life <= 0) {
        projectile.active = false;
        projectile.mesh.visible = false;
      }
    }
    for (const burst of this.bursts) {
      if (!burst.active) continue;
      burst.life -= dt * 1.35;
      for (let index = 0; index < burst.count; index += 1) {
        const offset = index * 3;
        burst.velocities[offset + 1] -= burst.gravity * dt;
        burst.positions[offset] += burst.velocities[offset] * dt;
        burst.positions[offset + 1] += burst.velocities[offset + 1] * dt;
        burst.positions[offset + 2] += burst.velocities[offset + 2] * dt;
      }
      burst.particles.geometry.attributes.position.needsUpdate = true;
      burst.particles.material.opacity = Math.max(0, burst.life);
      burst.particles.material.size = 3 + burst.life * 4;
      if (burst.life <= 0) {
        burst.active = false;
        burst.particles.visible = false;
        burst.particles.geometry.setDrawRange(0, 0);
      }
    }
  }

  get activeProjectileCount() {
    return this.projectiles.filter((projectile) => projectile.active).length;
  }

  get activeBurstCount() {
    return this.bursts.filter((burst) => burst.active).length;
  }
}
